import {
  CheckCircle as CheckIcon,
  DataObject as DataIcon,
  Error as ErrorIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { dataQualityValidator, type DataQualityIssue, type DataSchema } from '../services/dataQualityValidator.js';
import { dataSourceRegistry } from '../services/dataSourceRegistry.js';
import { WidgetProps } from '../types/index.js';

// Configuration interface
interface DataFetcherConfig {
  dataSourceKey?: string; // Key/name of the data source in the registry
  parameters?: Record<string, any>;
  refreshInterval?: number; // in seconds
}

// State interface
interface DataFetcherState {
  status: 'idle' | 'loading' | 'success' | 'error';
  error?: string;
  dataQualityIssues?: DataQualityIssue[];
  lastFetched?: string;
}

// Helper function to format timestamp
const formatTimestamp = (timestamp: string) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString();
};

// Helper function to render parameter input based on schema
const renderParameterInput = (
  key: string,
  schema: any,
  value: any,
  onChange: (key: string, value: any) => void
) => {
  const fieldSchema = schema?.fields?.[key];
  
  if (!fieldSchema) {
    // Default text field for unknown schemas
    return (
      <TextField
        key={key}
        label={key}
        value={value || ''}
        onChange={(e) => onChange(key, e.target.value)}
        fullWidth
        margin="normal"
        size="small"
      />
    );
  }

  switch (fieldSchema.type) {
    case 'number':
      return (
        <TextField
          key={key}
          label={key}
          type="number"
          value={value || ''}
          onChange={(e) => onChange(key, parseFloat(e.target.value))}
          fullWidth
          margin="normal"
          size="small"
          InputProps={{
            inputProps: {
              min: fieldSchema.constraints?.min,
              max: fieldSchema.constraints?.max,
              step: fieldSchema.constraints?.precision ? Math.pow(10, -fieldSchema.constraints.precision) : 0.01,
            },
          }}
        />
      );
    case 'boolean':
      return (
        <FormControl key={key} fullWidth margin="normal" size="small">
          <InputLabel>{key}</InputLabel>
          <Select
            value={value ?? false}
            onChange={(e) => onChange(key, e.target.value === 'true')}
            label={key}
          >
            <MenuItem value="true">True</MenuItem>
            <MenuItem value="false">False</MenuItem>
          </Select>
        </FormControl>
      );
    case 'array':
      return (
        <TextField
          key={key}
          label={`${key} (comma-separated)`}
          value={Array.isArray(value) ? value.join(', ') : value || ''}
          onChange={(e) => onChange(key, e.target.value.split(',').map(s => s.trim()))}
          fullWidth
          margin="normal"
          size="small"
          helperText="Enter comma-separated values"
        />
      );
    default:
      if (fieldSchema.constraints?.enum) {
        return (
          <FormControl key={key} fullWidth margin="normal" size="small">
            <InputLabel>{key}</InputLabel>
            <Select
              value={value || ''}
              onChange={(e) => onChange(key, e.target.value)}
              label={key}
            >
              {fieldSchema.constraints.enum.map((option: string) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }
      return (
        <TextField
          key={key}
          label={key}
          value={value || ''}
          onChange={(e) => onChange(key, e.target.value)}
          fullWidth
          margin="normal"
          size="small"
        />
      );
  }
  return null;
}

export const DataFetcherWidget: React.FC<WidgetProps> = ({
  widget,
  onUpdateConfig,
  onUpdateOutputs,
  onUpdateState,
}) => {
  const [dataPreviewOpen, setDataPreviewOpen] = useState(false);
  const [fetchedData, setFetchedData] = useState<any>(null);
  const [refreshIntervalId, setRefreshIntervalId] = useState<NodeJS.Timeout | null>(null);
  
  // Initialize test data source on first render and ensure sources are loaded
  React.useEffect(() => {
    console.log('[DataFetcherWidget] Checking data sources...');
    let sources = dataSourceRegistry.getAllSources();
    console.log('[DataFetcherWidget] Current sources:', sources);
    
    // If no sources, register a test one
    if (sources.length === 0) {
      console.log('[DataFetcherWidget] No sources found, registering test source...');
      dataSourceRegistry.register('test/mock', {
        type: 'api',
        endpoint: 'test',
        authentication: 'none',
        fetcher: async (params: any) => {
          return {
            message: 'Test data',
            timestamp: new Date().toISOString(),
            params: params
          };
        },
        cacheConfig: {
          ttl: 60000,
          maxSize: 10,
        },
      });
      console.log('[DataFetcherWidget] Registered test source');
      // Re-fetch sources after registration
      sources = dataSourceRegistry.getAllSources();
    }
    
    // Update the available sources state
    setAvailableSources(sources);
  }, []);
  
  const config = widget.config as DataFetcherConfig;
  
  // Use widget.state with proper typing and defaults
  const state: DataFetcherState = {
    status: (widget.state as any)?.status || 'idle',
    error: (widget.state as any)?.error,
    dataQualityIssues: (widget.state as any)?.dataQualityIssues,
    lastFetched: (widget.state as any)?.lastFetched,
  };

  // Get all available data sources - use state to ensure re-render when sources change
  const [availableSources, setAvailableSources] = useState<string[]>([]);

  // Get the schema for the selected data source
  const selectedSourceConfig = config.dataSourceKey 
    ? dataSourceRegistry.getSourceConfig(config.dataSourceKey)
    : undefined;

  // Handle data source selection
  const handleDataSourceChange = (source: string) => {
    onUpdateConfig({ 
      dataSourceKey: source,
      parameters: {} // Reset parameters when changing source
    });
    onUpdateState({ 
      ...widget.state,
      status: 'idle',
      error: undefined,
      dataQualityIssues: undefined 
    } as any);
    setFetchedData(null);
  };

  // Handle parameter changes
  const handleParameterChange = (key: string, value: any) => {
    onUpdateConfig({
      parameters: {
        ...config.parameters,
        [key]: value,
      },
    });
  };

  // Handle refresh interval change
  const handleRefreshIntervalChange = (interval: number) => {
    onUpdateConfig({ refreshInterval: interval });
  };

  // Fetch data function
  const fetchData = useCallback(async () => {
    if (!config.dataSourceKey) {
      onUpdateState({
        ...widget.state,
        status: 'error',
        error: 'No data source selected',
      } as any);
      return;
    }

    // Set loading state
    onUpdateState({
      ...widget.state,
      status: 'loading',
      error: undefined,
    } as any);

    try {
      // Fetch data from the registry
      const data = await dataSourceRegistry.fetch(
        config.dataSourceKey,
        config.parameters || {}
      );

      // Validate the data quality
      const sourceSchema = selectedSourceConfig?.schema as DataSchema | undefined;
      const validationResult = dataQualityValidator.validateFinancialData(
        data,
        sourceSchema
      );

      // Update state with results
      onUpdateState({
        ...widget.state,
        status: 'success',
        error: undefined,
        dataQualityIssues: validationResult.issues,
        lastFetched: new Date().toISOString(),
      } as any);

      // Store fetched data for preview
      setFetchedData(data);

      // Update outputs
      onUpdateOutputs({
        data: data,
        metadata: {
          source: config.dataSourceKey,
          timestamp: new Date().toISOString(),
          parameters: config.parameters,
          dataQuality: {
            score: validationResult.score,
            issues: validationResult.issues.length,
            warnings: validationResult.warnings.length,
          },
        },
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      onUpdateState({
        ...widget.state,
        status: 'error',
        error: errorMessage,
      } as any);
      setFetchedData(null);
    }
  }, [config.dataSourceKey, config.parameters, selectedSourceConfig, widget.state, onUpdateState, onUpdateOutputs]);

  // Setup auto-refresh
  useEffect(() => {
    // Clear existing interval
    if (refreshIntervalId) {
      clearInterval(refreshIntervalId);
    }

    // Setup new interval if configured
    if (config.refreshInterval && config.refreshInterval > 0 && config.dataSourceKey) {
      const intervalMs = config.refreshInterval * 1000;
      const id = setInterval(fetchData, intervalMs);
      setRefreshIntervalId(id);

      // Initial fetch
      fetchData();
    }

    // Cleanup
    return () => {
      if (refreshIntervalId) {
        clearInterval(refreshIntervalId);
      }
    };
  }, [config.refreshInterval, config.dataSourceKey]);

  // Get severity color
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  // Get status icon
  const getStatusIcon = () => {
    switch (state.status) {
      case 'success':
        return <CheckIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'loading':
        return <CircularProgress size={20} />;
      default:
        return null;
    }
  };

  // Debug: Show sources count
  console.log('[DataFetcherWidget Render] availableSources:', availableSources);

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <DataIcon color="primary" />
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Data Fetcher
        </Typography>
        {getStatusIcon()}
      </Box>

      {/* Configuration Panel */}
      <Box sx={{ mb: 2 }}>
        {/* Data Source Dropdown */}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="data-source-label">Data Source</InputLabel>
          <Select
            labelId="data-source-label"
            value={config.dataSourceKey || ''}
            onChange={(e) => handleDataSourceChange(e.target.value)}
            label="Data Source"
            displayEmpty
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {availableSources && availableSources.length > 0 ? (
              availableSources.map((source) => (
                <MenuItem key={source} value={source}>
                  {source}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                <em>Loading sources...</em>
              </MenuItem>
            )}
          </Select>
        </FormControl>

        {/* Dynamic Parameters Form */}
        {selectedSourceConfig && selectedSourceConfig.schema && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Parameters
            </Typography>
            {Object.keys((selectedSourceConfig.schema as any).fields || {}).map((key) =>
              renderParameterInput(
                key,
                selectedSourceConfig.schema,
                config.parameters?.[key],
                handleParameterChange
              )
            )}
          </Box>
        )}

        {/* Refresh Interval */}
        <TextField
          label="Auto-refresh interval (seconds)"
          type="number"
          value={config.refreshInterval || 0}
          onChange={(e) => handleRefreshIntervalChange(parseInt(e.target.value) || 0)}
          fullWidth
          size="small"
          helperText="Set to 0 to disable auto-refresh"
          InputProps={{
            inputProps: { min: 0, max: 3600 },
          }}
        />
      </Box>

      {/* Action Buttons */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchData}
          disabled={!config.dataSourceKey || state.status === 'loading'}
          fullWidth
        >
          Fetch Data
        </Button>
      </Box>

      {/* Status and Feedback */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {/* Loading State */}
        {state.status === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error Display */}
        {state.status === 'error' && state.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {state.error}
          </Alert>
        )}

        {/* Data Quality Warnings */}
        {state.dataQualityIssues && state.dataQualityIssues.length > 0 && (
          <Alert 
            severity="warning" 
            sx={{ mb: 2 }}
            icon={<WarningIcon />}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Data Quality Issues ({state.dataQualityIssues.length})
            </Typography>
            <Stack spacing={0.5}>
              {state.dataQualityIssues.slice(0, 3).map((issue, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    label={issue.severity} 
                    size="small" 
                    color={getSeverityColor(issue.severity) as any}
                  />
                  <Typography variant="body2">
                    {issue.field}: {issue.description}
                  </Typography>
                </Box>
              ))}
              {state.dataQualityIssues.length > 3 && (
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  ...and {state.dataQualityIssues.length - 3} more issues
                </Typography>
              )}
            </Stack>
          </Alert>
        )}

        {/* Success Indicator */}
        {state.status === 'success' && state.lastFetched && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Data fetched successfully at {formatTimestamp(state.lastFetched)}
            </Typography>
          </Alert>
        )}

        {/* Data Preview */}
        {fetchedData && (
          <Paper sx={{ p: 1 }}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                mb: dataPreviewOpen ? 1 : 0
              }}
              onClick={() => setDataPreviewOpen(!dataPreviewOpen)}
            >
              <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                Data Preview
              </Typography>
              <IconButton size="small">
                {dataPreviewOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={dataPreviewOpen}>
              <Divider sx={{ mb: 1 }} />
              <Box 
                sx={{ 
                  maxHeight: 200, 
                  overflow: 'auto',
                  backgroundColor: 'background.default',
                  p: 1,
                  borderRadius: 1,
                }}
              >
                <Typography 
                  variant="body2" 
                  component="pre"
                  sx={{ 
                    margin: 0,
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                  }}
                >
                  {JSON.stringify(fetchedData, null, 2)}
                </Typography>
              </Box>
            </Collapse>
          </Paper>
        )}
      </Box>
    </Box>
  );
};